class StatisticsWithTabs extends HTMLElement {
  constructor() {
    super();
    // Bind methods that will be used as callbacks
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleTabClick = this.handleTabClick.bind(this);

    // Initialize intersection observer
    this.observer = new IntersectionObserver(this.handleIntersection, {
      threshold: 0.5 // Trigger when 50% of element is visible
    });
  }

  connectedCallback() {
    // Cache DOM elements
    this.initializeElements();

    if (!this.isValid()) return;

    // Set up observers and event listeners
    this.setupEventListeners();

    // Initialize state
    this.resetProgressBars();
    this.observer.observe(this.animatedSection);
  }

  disconnectedCallback() {
    // Clean up all observers and listeners
    this.observer.disconnect();
    this.removeEventListeners();
  }

  /**
   * Initialize and cache all required DOM elements
   * @private
   */
  initializeElements() {
    this.animatedSection = this.querySelector('[data-animated-section="circular-progress"]');
    this.progressBars = this.querySelectorAll('.circular-progress');
    this.tabs = this.querySelectorAll('.js-tab-item');
    this.statsSections = this.querySelectorAll('.statistics-with-tabs__stats');
  }

  /**
   * Validate that all required elements are present
   * @private
   * @returns {boolean}
   */
  isValid() {
    return this.animatedSection && this.progressBars.length > 0;
  }

  /**
   * Set up all event listeners
   * @private
   */
  setupEventListeners() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', this.handleTabClick);
    });
  }

  /**
   * Remove all event listeners
   * @private
   */
  removeEventListeners() {
    this.tabs.forEach(tab => {
      tab.removeEventListener('click', this.handleTabClick);
    });
  }

  /**
   * Handle intersection observer callback
   * @private
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    const [entry] = entries;
    if (entry.isIntersecting && !this.hasAnimated) {
      this.hasAnimated = true;
      this.startAnimation();
    }
  }

  /**
   * Reset all progress bars to initial state
   * @private
   */
  resetProgressBars() {
    this.progressBars.forEach(bar => {
      const progress = bar.querySelector('.circular-progress__progress');
      progress?.classList.remove('animate');
    });
  }

  /**
   * Start the progress bar animations
   * @private
   */
  startAnimation() {
    this.progressBars.forEach(bar => {
      const progress = bar.querySelector('.circular-progress__progress');
      const inner = bar.querySelector('.circular-progress__inner');

      if (!progress || !inner) return;

      const value = parseInt(inner.textContent, 10);
      if (isNaN(value)) return;

      progress.style.setProperty('--progress', value);
      progress.classList.add('animate');
    });
  }

  /**
   * Handle tab click events
   * @private
   * @param {Event} event
   */
  handleTabClick(event) {
    const clickedTab = event.currentTarget;
    const tabTrackName = clickedTab.dataset.trackName;
    const activeTabClass = 'statistics-with-tabs__tab-active';

    // Update tab active states
    this.tabs.forEach(tab => {
      tab.classList.remove(activeTabClass);
    });
    clickedTab.classList.add(activeTabClass);

    // Update stats sections
    this.statsSections.forEach(section => {
      const isActive = section.dataset.trackSection === tabTrackName;
      section.classList.toggle('active', isActive);
    });

    // Reset and restart animation
    this.hasAnimated = false;
    this.resetProgressBars();

    // Use requestAnimationFrame for smoother animation
    requestAnimationFrame(() => {
      this.hasAnimated = true;
      this.startAnimation();
    });
  }
}

customElements.define('statistics-with-tabs', StatisticsWithTabs);